import { useState, useMemo } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Slider,
    Switch,
    FormControlLabel,
    Divider,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
} from '@mui/material'
import { Calculate } from '@mui/icons-material'
import {
    EVENT_TIERS,
    AI_PRICING,
    resolveIngestLimitPolicy,
    estimateMonthlyCost,
    calculateEventCost,
    calculateAiRunCost,
    formatCurrency,
    formatNumber,
    tierPricePerK,
    runTierPrice,
    type SupportedCurrency,
} from '@yourownmetric/shared'

// --- Event slider: piecewise linear with milestone snap points ---

interface Milestone {
    position: number
    value: number
    label: string
}

const EVENT_MILESTONES: Milestone[] = [
    { position: 0, value: 0, label: '0' },
    { position: 10, value: 100_000, label: '100K' },
    { position: 20, value: 500_000, label: '500K' },
    { position: 30, value: 1_000_000, label: '1M' },
    { position: 40, value: 2_000_000, label: '2M' },
    { position: 50, value: 5_000_000, label: '5M' },
    { position: 60, value: 10_000_000, label: '10M' },
    { position: 70, value: 20_000_000, label: '20M' },
    { position: 80, value: 50_000_000, label: '50M' },
    { position: 90, value: 75_000_000, label: '75M' },
    { position: 100, value: 100_000_000, label: '100M' },
]

const EVENT_MARKS = EVENT_MILESTONES.map(m => ({ value: m.position, label: m.label }))

const AI_RUN_MILESTONES: Milestone[] = [
    { position: 0, value: 0, label: '0' },
    { position: 25, value: 50, label: '50' },
    { position: 50, value: 100, label: '100' },
    { position: 75, value: 500, label: '500' },
    { position: 100, value: 1000, label: '1K' },
]

const AI_RUN_MARKS = AI_RUN_MILESTONES.map(m => ({ value: m.position, label: m.label }))

/** Piecewise linear interpolation: slider position → actual value */
function scaleFromSlider(sliderValue: number, milestones: Milestone[]): number {
    for (let i = 1; i < milestones.length; i++) {
        if (sliderValue <= milestones[i].position) {
            const prev = milestones[i - 1]
            const next = milestones[i]
            const ratio = (sliderValue - prev.position) / (next.position - prev.position)
            return Math.round(prev.value + ratio * (next.value - prev.value))
        }
    }
    return milestones[milestones.length - 1].value
}

/** Inverse: actual value → slider position */
function scaleToSlider(value: number, milestones: Milestone[]): number {
    for (let i = 1; i < milestones.length; i++) {
        if (value <= milestones[i].value) {
            const prev = milestones[i - 1]
            const next = milestones[i]
            if (next.value === prev.value) {
                return prev.position
            }
            const ratio = (value - prev.value) / (next.value - prev.value)
            return Math.round(prev.position + ratio * (next.position - prev.position))
        }
    }
    return 100
}

export function PricingCalculator({ currency = 'USD' }: { currency?: SupportedCurrency }) {
    const [eventSlider, setEventSlider] = useState(scaleToSlider(500_000, EVENT_MILESTONES))
    const [aiEnabled, setAiEnabled] = useState(false)
    const [aiRunSlider, setAiRunSlider] = useState(scaleToSlider(50, AI_RUN_MILESTONES))

    const events = scaleFromSlider(eventSlider, EVENT_MILESTONES)
    const aiRuns = scaleFromSlider(aiRunSlider, AI_RUN_MILESTONES)

    const fmt = (cents: number) => formatCurrency(cents, currency)
    const freePolicy = resolveIngestLimitPolicy('free')
    const paygPolicy = resolveIngestLimitPolicy('payg')

    const breakdown = useMemo(() => {
        const eventCost = calculateEventCost(events, currency)
        const aiCost = aiEnabled ? calculateAiRunCost(aiRuns, currency) : 0
        const total = estimateMonthlyCost({ events, aiEnabled, aiRuns }, currency)

        const tiers = EVENT_TIERS.map((tier, i) => {
            const prevUpTo = i > 0 ? EVENT_TIERS[i - 1].upTo : 0
            const tierCapacity = tier.upTo - prevUpTo
            const eventsInTier = Math.min(Math.max(0, events - prevUpTo), tierCapacity)
            const cost = (eventsInTier / 1000) * tierPricePerK(tier, currency)
            return { ...tier, eventsInTier, cost: Math.round(cost), prevUpTo }
        }).filter(t => t.eventsInTier > 0)

        // AI run tier breakdown
        const aiRunTiers = aiEnabled
            ? AI_PRICING.runTiers
                  .map((tier, i) => {
                      const prevUpTo = i > 0 ? AI_PRICING.runTiers[i - 1].upTo : 0
                      const tierCapacity = tier.upTo - prevUpTo
                      const runsInTier = Math.min(Math.max(0, aiRuns - prevUpTo), tierCapacity)
                      const cost = runsInTier * runTierPrice(tier, currency)
                      return { ...tier, runsInTier, cost: Math.round(cost), prevUpTo }
                  })
                  .filter(t => t.runsInTier > 0)
            : []

        return { eventCost, aiCost, total, tiers, aiRunTiers }
    }, [events, aiEnabled, aiRuns, currency])

    return (
        <Box sx={{ mt: 5 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                <Calculate sx={{ fontSize: 20, mr: 1, verticalAlign: 'text-bottom' }} />
                Pricing Calculator
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Estimate your monthly cost based on expected usage.
            </Typography>

            <Card>
                <CardContent>
                    <Box
                        sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}
                    >
                        {/* Sliders */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Monthly Events: <strong>{formatNumber(events)}</strong>
                            </Typography>
                            <Slider
                                value={eventSlider}
                                onChange={(_, v) => setEventSlider(v as number)}
                                min={0}
                                max={100}
                                step={1}
                                marks={EVENT_MARKS}
                                valueLabelDisplay="auto"
                                valueLabelFormat={() => formatNumber(events)}
                                sx={{
                                    mb: 3,
                                    '& .MuiSlider-markLabel': {
                                        fontSize: '0.65rem',
                                        transform: 'translateX(-50%) rotate(-45deg)',
                                        transformOrigin: 'top center',
                                        mt: 1,
                                    },
                                }}
                            />

                            <Divider sx={{ my: 2 }} />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={aiEnabled}
                                        onChange={e => setAiEnabled(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="subtitle2">
                                        AI Insights Add-on ({fmt(AI_PRICING.retainer[currency])}
                                        /mo)
                                    </Typography>
                                }
                            />

                            {aiEnabled && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        AI Agent Runs / Month: <strong>{aiRuns}</strong>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {AI_PRICING.includedRuns} runs included, then graduated
                                        pricing
                                    </Typography>
                                    <Slider
                                        value={aiRunSlider}
                                        onChange={(_, v) => setAiRunSlider(v as number)}
                                        min={0}
                                        max={100}
                                        step={1}
                                        marks={AI_RUN_MARKS}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={() => String(aiRuns)}
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Result */}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                p: 3,
                            }}
                        >
                            <Typography variant="overline" color="text.secondary">
                                Estimated Monthly Cost
                            </Typography>
                            <Typography
                                variant="h3"
                                sx={{ fontWeight: 700, my: 1, color: 'primary.main' }}
                            >
                                {fmt(breakdown.total.total)}
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Service policy
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Free plan limit windows: daily
                                    {freePolicy.dailyLimit
                                        ? ` ${formatNumber(freePolicy.dailyLimit)}`
                                        : ' (not fixed)'}
                                    , weekly
                                    {freePolicy.weeklyLimit
                                        ? ` ${formatNumber(freePolicy.weeklyLimit)}`
                                        : ' (not fixed)'}
                                    , monthly
                                    {freePolicy.monthlyLimit
                                        ? ` ${formatNumber(freePolicy.monthlyLimit)}`
                                        : ' (not fixed)'}
                                    .
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    After hard limits, free-plan processing may run in degraded mode
                                    (higher latency and reduced advanced processing) until usage
                                    resets.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Pay-as-you-go default: no fixed daily/weekly/monthly hard cap,
                                    subject to platform safety controls and abuse protection.
                                </Typography>
                                {paygPolicy.absoluteDailyLimit && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Platform safety cap may apply for extreme spikes.
                                    </Typography>
                                )}
                            </Box>

                            {/* Tier breakdown table */}
                            {breakdown.tiers.length > 0 && (
                                <Table size="small" sx={{ mt: 2 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Tier</TableCell>
                                            <TableCell>Events</TableCell>
                                            <TableCell align="right">Cost</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {breakdown.tiers.map(t => (
                                            <TableRow key={t.label}>
                                                <TableCell>
                                                    {tierPricePerK(t, currency) === 0 ? (
                                                        <Chip
                                                            label={t.label}
                                                            size="small"
                                                            color="success"
                                                        />
                                                    ) : (
                                                        t.label
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatNumber(t.eventsInTier)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {tierPricePerK(t, currency) === 0
                                                        ? 'Free'
                                                        : fmt(t.cost)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {aiEnabled && breakdown.aiRunTiers.length > 0 && (
                                            <>
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={3}
                                                        sx={{ pt: 2, fontWeight: 600 }}
                                                    >
                                                        AI Agent Runs ({aiRuns} runs)
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell>Retainer</TableCell>
                                                    <TableCell>&mdash;</TableCell>
                                                    <TableCell align="right">
                                                        {fmt(AI_PRICING.retainer[currency])}
                                                    </TableCell>
                                                </TableRow>
                                                {breakdown.aiRunTiers.map(t => (
                                                    <TableRow key={`ai-${t.label}`}>
                                                        <TableCell>
                                                            {runTierPrice(t, currency) === 0 ? (
                                                                <Chip
                                                                    label={t.label}
                                                                    size="small"
                                                                    color="success"
                                                                />
                                                            ) : (
                                                                t.label
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{t.runsInTier} runs</TableCell>
                                                        <TableCell align="right">
                                                            {runTierPrice(t, currency) === 0
                                                                ? 'Free'
                                                                : fmt(t.cost)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </>
                                        )}
                                        {aiEnabled && breakdown.aiRunTiers.length === 0 && (
                                            <TableRow>
                                                <TableCell>AI Retainer</TableCell>
                                                <TableCell>0 runs</TableCell>
                                                <TableCell align="right">
                                                    {fmt(AI_PRICING.retainer[currency])}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell colSpan={2}>
                                                <Typography variant="subtitle2">Total</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="subtitle2"
                                                    color="primary.main"
                                                >
                                                    {fmt(breakdown.total.total)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}
